import { Button } from "./ui/button";
import logo from "@/assets/logo.png";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { LogIn, LogOut, BookOpen, Bell, Menu } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "./ui/sheet";
import ThemeToggle from "./ThemeToggle";
import SocialIconsBar from "./SocialIconsBar";

interface DbNotification {
  id: string;
  user_id: string;
  type: string;
  message: string;
  reference_id: string | null;
  is_read: boolean;
  created_at: string;
}

const Navbar = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<DbNotification[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchNotifications = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);
      setNotifications((data as any[]) || []);
    };
    fetchNotifications();

    // Subscribe to realtime notifications.
    // RLS ensures only this user's notifications stream through; the user_id check is defense in depth.
    const channel = supabase
      .channel("user-notifications")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications" }, (payload) => {
        const newNotif = payload.new as DbNotification;
        if (newNotif.user_id === user.id) {
          setNotifications((prev) => [{ ...newNotif }, ...prev].slice(0, 20));
        }
      })
      .subscribe((status, err) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.error("Notifications realtime subscription failed:", status, err);
        }
      });

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleOpenNotifications = async (isOpen: boolean) => {
    setOpen(isOpen);
  };

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;
    await supabase.functions.invoke("manage-certificates", {
      body: { action: "mark_notifications_read" },
    });
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const markOneRead = async (notifId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
    );
    await supabase.from("notifications").update({ is_read: true }).eq("id", notifId);
  };

  const handleNotificationClick = async (n: DbNotification) => {
    setOpen(false);
    if (!n.is_read) markOneRead(n.id);

    const ref = n.reference_id;
    try {
      switch (n.type) {
        case "enrollment_granted":
        case "enrollment_rejected":
        case "enrollment_request":
          // ref = course_id → open the course
          if (ref) navigate(`/dashboard?course=${ref}`);
          else navigate("/dashboard");
          break;
        case "certificate_approved":
        case "certificate_rejected":
          navigate("/dashboard");
          break;
        case "lesson_qa_reply": {
          // ref = lesson_id → look up its course and open
          if (!ref) { navigate("/dashboard"); break; }
          const { data } = await supabase.from("lessons").select("course_id").eq("id", ref).maybeSingle();
          if (data?.course_id) navigate(`/dashboard?course=${data.course_id}&lesson=${ref}`);
          else navigate("/dashboard");
          break;
        }
        case "policy_warning":
          navigate("/dashboard");
          break;
        default:
          navigate("/dashboard");
      }
    } catch {
      navigate("/dashboard");
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src={logo} alt="Fitawrari FX" className="h-8 sm:h-10 w-auto shrink-0 dark:invert dark:brightness-200 dark:contrast-100 transition-[filter]" />
          <span className="text-base sm:text-xl font-display font-semibold text-foreground truncate">
            Fitawrari FX
          </span>
        </a>
        
        <div className="hidden md:flex items-center gap-6">
          <a href="/" className="text-muted-foreground hover:text-foreground transition-colors">Home</a>
          <a href="/#free-course" className="text-muted-foreground hover:text-foreground transition-colors">Free Material</a>
          <a href="/#education" className="text-muted-foreground hover:text-foreground transition-colors">Education</a>
          <a href="/#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
          <a href="/#certification" className="text-muted-foreground hover:text-foreground transition-colors">Certification</a>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-3">
          <SocialIconsBar size={16} className="hidden lg:flex mr-2" iconClassName="hover:text-foreground" />
          <ThemeToggle />

          {user ? (
            <div className="flex items-center gap-1 sm:gap-2">
              <Popover open={open} onOpenChange={handleOpenNotifications}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="relative px-2">
                    <Bell className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] rounded-full flex items-center justify-center font-bold">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="p-3 border-b border-border flex items-center justify-between">
                    <h4 className="font-semibold text-sm">Notifications</h4>
                    {unreadCount > 0 && (
                      <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={handleMarkAllRead}>
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground text-center">No notifications</p>
                    ) : (
                      notifications.map((n) => (
                        <div key={n.id} onClick={() => handleNotificationClick(n)} className={`px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer ${!n.is_read ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}>
                          <div className="flex items-start gap-2">
                            {!n.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />}
                            <div className={!n.is_read ? "" : "ml-4"}>
                              <p className={`text-sm ${!n.is_read ? "font-semibold" : "text-muted-foreground"}`}>{n.message}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {new Date(n.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} className="hidden sm:inline-flex">
                <BookOpen className="w-4 h-4 mr-1" /> My Courses
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate("/p2p")} className="hidden sm:inline-flex">
                P2P
              </Button>
              <Button variant="ghost" size="sm" onClick={signOut} className="hidden sm:inline-flex px-2">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate("/auth")}>
                <LogIn className="w-4 h-4 mr-1" /> Sign In
              </Button>
              <Button size="sm" onClick={() => navigate("/auth", { state: { isSignUp: true } })}>
                Sign Up
              </Button>
            </div>
          )}

          {/* Mobile menu — always last on the right */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden px-2" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <img src={logo} alt="Fitawrari FX" className="h-8 w-auto dark:invert dark:brightness-200 dark:contrast-100" />
                  <span className="font-display">Fitawrari FX</span>
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1">
                {[
                  { href: "/", label: "Home" },
                  { href: "/#free-course", label: "Free Material" },
                  { href: "/#education", label: "Education" },
                  { href: "/#pricing", label: "Pricing" },
                  { href: "/#certification", label: "Certification" },
                ].map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    className="px-3 py-3 rounded-md text-foreground hover:bg-muted transition-colors"
                  >
                    {l.label}
                  </a>
                ))}
                {user ? (
                  <>
                    <Button variant="outline" className="mt-3" onClick={() => navigate("/dashboard")}>
                      <BookOpen className="w-4 h-4 mr-2" /> My Courses
                    </Button>
                    <Button variant="outline" className="mt-1" onClick={() => navigate("/p2p")}>
                      P2P USDT ⇄ ETB
                    </Button>
                    <Button variant="ghost" className="mt-1" onClick={signOut}>
                      <LogOut className="w-4 h-4 mr-2" /> Sign Out
                    </Button>
                  </>
                ) : (
                  <div className="mt-3 flex flex-col gap-2">
                    <Button variant="outline" onClick={() => navigate("/auth")}>
                      <LogIn className="w-4 h-4 mr-2" /> Sign In
                    </Button>
                    <Button onClick={() => navigate("/auth", { state: { isSignUp: true } })}>
                      Sign Up
                    </Button>
                  </div>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
